import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { Lock, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * Wrap a route element to enforce role-permission access.
 * Renders a friendly "Access Restricted" screen when the current user
 * lacks `${module}.${action}`. Admin always passes.
 *
 * Usage:
 *   <PermGuard module="customers" action="view"><Customers /></PermGuard>
 */
export default function PermGuard({ module, action = "view", children }) {
  const { user, can, permissions } = useAuth();

  // Still loading session → let the parent Protected route show its spinner.
  if (user === null) return null;
  if (user?.role === "hr") return <Navigate to="/my-hrms" replace />;

  // Wait for permissions to hydrate before evaluating (empty map = loading).
  const permsLoaded = permissions && Object.keys(permissions).length > 0;
  if (!permsLoaded && user && user.role !== "admin") return null;

  if (can(module, action)) return children;

  return (
    <div className="page-shell min-h-[60vh] flex items-center justify-center" data-testid="perm-denied">
      <div className="max-w-md text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-6">
          <Lock size={28} weight="bold" />
        </div>
        <div className="eyebrow text-destructive mb-2">Access Restricted</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-3">
          You don't have permission to view this page.
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          The <span className="font-semibold text-foreground">{module}</span> module has been disabled for your role in this company.
          Please ask an <span className="font-semibold text-foreground">Admin</span> to enable it from
          <span className="font-mono"> Administration → Permissions</span>.
        </p>
        <div className="flex items-center justify-center gap-2">
          <Link to="/dashboard">
            <Button variant="outline" className="h-9" data-testid="perm-denied-back">
              <ArrowLeft size={14} className="mr-1.5" /> Back to Dashboard
            </Button>
          </Link>
        </div>
        <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          Signed in as <span className="font-mono text-foreground">{user?.email}</span> · Role <span className="font-mono text-foreground">{user?.role}</span>
        </div>
      </div>
    </div>
  );
}
